import React from 'react';
import { Sparkles, MessageCircleHeart, ChevronRight } from 'lucide-react';

// Shown whenever there is nothing personal to say: no AI consent, a guest, no
// entry in the last few days, a heavy day, or while nothing has loaded. Also
// becomes the AI's first chat line on "Open Dialogue" in that case, so the
// card and the chat always agree. Kept in the client on purpose: copy changes
// need no backend deploy.
export const GENERIC_OPENER_QUESTION = "What's been on your mind lately?";

/**
 * The AI's first chat message for a personal opener. Must match
 * composeOpenerMessage in backend/services/reflections/companionOpener.js,
 * which is what the server saves. If the two drift, a resumed chat reads
 * differently from the one the user saw.
 */
export function composeOpenerMessage({ acknowledgement, question }) {
  return `${acknowledgement} ${question}`.trim();
}

/**
 * The memory line's lead-in: "From earlier today" / "Remembering yesterday" /
 * "Remembering Tuesday". Computed here, in the user's own timezone, rather
 * than written by the model: the opener is cached, and a day word inside it
 * would be wrong by tomorrow.
 * @param {string} sourceDate YYYY-MM-DD
 * @param {Date} [now]
 */
export function memoryLabel(sourceDate, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(sourceDate || '');
  if (!m) return null;
  const then = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today - then) / 86400000);
  if (days <= 0) return 'From earlier today';
  if (days === 1) return 'Remembering yesterday';
  const weekday = new Date(then).toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });
  return `Remembering ${weekday}`;
}

// What sits under the headline when there is no memory line, keyed by the
// server's `reason`. 'quiet' (a heavy day, a guest, a model answer we would
// not show) deliberately has no hint: the card never explains a silence it
// chose.
const GENERIC_HINT = {
  no_recent: "Write a few lines or record an Echo, and I'll pick up from there.",
  no_consent: "Turn on AI personalization in Profile, and I'll pick up from what you write.",
};

// Sparkles drifting across the halo: [left %, top %, size px, delay s].
const MOTES = [
  [20, 28, 5, 0], [72, 20, 4, 1.4], [80, 58, 6, 2.6], [28, 74, 4, 0.8],
  [55, 12, 3, 3.1], [12, 50, 3, 2.0], [64, 82, 4, 1.1], [40, 18, 3, 2.2],
  [86, 38, 3, 0.5], [46, 88, 3, 3.6],
];

/**
 * Home Companion card: picks up the user's latest entry.
 *
 *   orb + "Tap to Chat"  → a fresh chat, composer focused (onTapToChat)
 *   headline (question)  → the follow-up, written about the latest entry
 *   memory line          → "Remembering yesterday: <acknowledgement>"
 *   Open Dialogue        → chat that opens with the AI already asking the
 *                          headline (onOpenDialogue)
 *
 * Presentational only. App owns the query and both handlers. `opener` is the
 * GET /user/:id/companion-opener payload, or undefined while loading.
 */
export default function CompanionCard({ opener, loading, onTapToChat, onOpenDialogue }) {
  const personal = opener?.kind === 'personal';
  const label = personal ? memoryLabel(opener.sourceDate) : null;
  const hint = personal ? null : GENERIC_HINT[opener?.reason];
  const question = personal ? opener.question : GENERIC_OPENER_QUESTION;

  return (
    <section
      aria-label="AI companion"
      className="relative overflow-hidden rounded-[2rem] border border-stone-200/70 bg-[#FFFEFB] px-6 pb-7 pt-6 shadow-[0_10px_36px_-14px_rgba(60,50,40,0.22)]"
    >
      <style>{`
        @keyframes cc-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes cc-glow { 0%, 100% { opacity: .8; transform: scale(.94); } 50% { opacity: 1; transform: scale(1.06); } }
        @keyframes cc-ripple { 0% { opacity: 0; transform: scale(.62); } 15% { opacity: .75; } 100% { opacity: 0; transform: scale(1.02); } }
        @keyframes cc-twinkle { 0%, 100% { opacity: 0; transform: translateY(3px) scale(.6); } 50% { opacity: 1; transform: translateY(-4px) scale(1); } }
        .cc-orb { animation: cc-breathe 6.5s ease-in-out infinite; }
        .cc-core { animation: cc-glow 6.5s ease-in-out infinite; }
        .cc-ripple { animation: cc-ripple 3.25s ease-out infinite; }
        .cc-ripple-2 { animation-delay: 1.62s; }
        .cc-mote { animation: cc-twinkle 4.8s ease-in-out infinite both; }
        @media (prefers-reduced-motion: reduce) {
          .cc-orb, .cc-core, .cc-mote, .cc-ripple { animation: none; }
          .cc-ripple { opacity: .45; transform: scale(.8); }
          .cc-ripple-2 { display: none; }
          .cc-mote { opacity: .7; }
        }
      `}</style>

      {/* Warm wash: white top-left into pale amber top-right, as in the design. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_70%_at_100%_0%,rgba(250,236,210,0.75),transparent_60%),radial-gradient(90%_60%_at_0%_0%,#FFFFFF,transparent_70%)]"
      />

      <div className="relative flex flex-col items-center text-center">
        {/* Orb. The whole orb is the button; the pill is its visible label. */}
        <button
          type="button"
          onClick={onTapToChat}
          aria-label="Tap to chat: start a new conversation with your companion"
          className="group relative mb-5 mt-1 flex flex-col items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-4 focus-visible:ring-offset-[#FFFEFB]"
        >
          <span className="cc-orb relative block h-52 w-52 rounded-full transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-active:scale-[0.98]">
            {/* lavender halo → violet band → amber body → white-hot core */}
            <span aria-hidden="true" className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(190,168,228,0.85)_0%,rgba(196,176,230,0.6)_48%,rgba(214,200,236,0)_72%)] blur-[3px]" />
            <span aria-hidden="true" className="absolute inset-[16%] rounded-full bg-[radial-gradient(circle,rgba(172,142,214,0)_52%,rgba(172,142,214,0.55)_70%,rgba(172,142,214,0)_100%)]" />
            <span aria-hidden="true" className="absolute inset-[22%] rounded-full bg-[radial-gradient(circle,rgba(245,170,110,1)_0%,rgba(240,176,128,0.9)_38%,rgba(226,170,170,0.55)_62%,rgba(200,180,230,0)_80%)]" />
            <span aria-hidden="true" className="cc-ripple absolute inset-[4%] rounded-full border-[1.5px] border-[#E39A86]" />
            <span aria-hidden="true" className="cc-ripple cc-ripple-2 absolute inset-[4%] rounded-full border-[1.5px] border-[#E39A86]" />
            <span aria-hidden="true" className="cc-core absolute inset-[38%] rounded-full bg-[radial-gradient(circle,#FFFFFF_0%,rgba(255,248,236,0.95)_45%,rgba(255,236,210,0)_100%)] shadow-[0_0_36px_14px_rgba(255,244,228,0.95)]" />
            {MOTES.map(([x, y, s, d]) => (
              <span
                key={`${x}-${y}`}
                aria-hidden="true"
                className="cc-mote absolute rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,1)]"
                style={{ left: `${x}%`, top: `${y}%`, width: s, height: s, animationDelay: `${d}s` }}
              />
            ))}
          </span>
          <span className="-mt-5 rounded-full border border-stone-200/80 bg-white/90 px-5 py-1.5 text-[14px] font-medium text-stone-500 shadow-sm backdrop-blur-sm transition-colors duration-300 ease-out group-hover:text-stone-700">
            Tap to Chat
          </span>
        </button>

        <div className="mb-3 flex items-center gap-2 text-[#A0561C]">
          <Sparkles size={16} strokeWidth={2} aria-hidden="true" />
          <span className="text-[11px] font-bold uppercase tracking-[0.18em]">
            AI Companion{personal ? ' · Remembers you' : ''}
          </span>
        </div>

        {loading ? (
          <div className="flex w-full flex-col items-center gap-2.5" aria-hidden="true">
            <div className="h-5 w-4/5 animate-pulse rounded-full bg-stone-100" />
            <div className="h-5 w-3/5 animate-pulse rounded-full bg-stone-100" />
            <div className="mt-2 h-3.5 w-11/12 animate-pulse rounded-full bg-stone-100/80" />
            <div className="h-3.5 w-2/3 animate-pulse rounded-full bg-stone-100/80" />
          </div>
        ) : (
          <>
            <h3 className="max-w-[26rem] text-[1.2rem] font-semibold leading-snug text-stone-800">
              “{question}”
            </h3>
            {personal && (
              <p className="mt-3 line-clamp-3 max-w-[26rem] font-serif text-[1rem] italic leading-relaxed text-stone-500">
                {label && `${label}: `}
                {opener.acknowledgement}
              </p>
            )}
            {hint && (
              <p className="mt-3 max-w-[24rem] text-[0.9rem] leading-relaxed text-stone-400">{hint}</p>
            )}
          </>
        )}

        <button
          type="button"
          onClick={onOpenDialogue}
          disabled={loading}
          aria-label={personal ? 'Open dialogue: continue from your latest entry' : 'Open dialogue with your companion'}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2D2A27] px-6 py-3 text-[15px] font-medium text-[#F0EDE8] shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-2"
        >
          <MessageCircleHeart size={18} strokeWidth={2} className="text-[#E8B86D]" aria-hidden="true" />
          Open Dialogue
          <ChevronRight size={17} className="text-stone-400" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
